import { t } from "@travily/i18n";
import { accountSettingsPath, creatorListingsPath, favoritesPath, messagesPath } from "@travily/routes";
import type ImageType from "@travily/types/shared/Image";
import {
  DropMenu,
  DropMenuItem,
  DropMenuSection,
  DropMenuSectionHeader,
  DropMenuSeparator,
} from "@ui/components/DropMenu";
import { Icon } from "@ui/components/Icon";
import { NAV_PREFETCH_CACHE_FOR } from "@ui/lib/prefetch-config";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";

export interface AccountSwitcherAccount {
  /** Unique account identifier. */
  id: number;
  /** URL-safe handle for the account. */
  handle: string | null;
  /** Full account name. */
  name: string | null;
  /** Structured avatar image. Falls back to initial when null. */
  avatar: ImageType | null;
  /** Profile type (Family, Individual, Community) for color mapping. */
  profile_type: string;
  /** Single character shown when no avatar image is available. */
  initial: string | null;
  /** Name displayed in the switcher menu. */
  display_name: string | null;
}

/** Props for the account-switching portion of the app menu. */
export interface AccountSwitcherProps {
  /** The currently active account, shown with a "View profile" link. */
  currentAccount?: AccountSwitcherAccount;
  /** Other accounts available for switching. */
  otherAccounts: AccountSwitcherAccount[];
  /** URL for the account-type picker. Pressing "Add new account" navigates here. */
  addAccountHref: string;
  /** URL for the current account's profile page. */
  profileHref?: string;
  /** Initial letter of the signed-in user. */
  userInitial?: string;
  /** Called when the user selects a different account. */
  onSwitchAccount: (accountId: number) => void;
}

// Desktop dropdown: prefetch item targets on hover (mount would fan out a
// request per item every time the popover opens).

export interface AppMenuProps {
  accountSwitcher: AccountSwitcherProps;
  /** Controls which mode menu item to show. Defaults to "explorer". */
  mode?: "explorer" | "creator";
  /** Called when the "Languages & currency" menu item is pressed. */
  onLanguagesAndCurrency?: () => void;
  /** Called when "Get Help" is pressed (opens the support messenger). */
  onGetHelp?: () => void;
  /** Called when "Log out" is pressed. */
  onLogout?: () => void;
  /** Unread message count; badges the "Messages" item when above zero. */
  unreadMessagesCount?: number;
}

export function AppMenu({
  accountSwitcher,
  mode = "explorer",
  onLanguagesAndCurrency,
  onGetHelp,
  onLogout,
  unreadMessagesCount = 0,
}: AppMenuProps) {
  const { currentAccount, otherAccounts, addAccountHref, profileHref, onSwitchAccount } = accountSwitcher;

  return (
    <DropMenu>
      {currentAccount && (
        <DropMenuItem
          cacheFor={NAV_PREFETCH_CACHE_FOR}
          href={profileHref}
          prefetch="hover"
          icon={<AccountAvatar account={currentAccount} shape="square" size="20" />}
        >
          {t("components.frames.account_switcher.view_profile")}
        </DropMenuItem>
      )}
      <DropMenuSection>
        {otherAccounts.length > 0 && (
          <DropMenuSectionHeader>{t("components.frames.account_switcher.switch")}</DropMenuSectionHeader>
        )}
        {otherAccounts.map((account) => (
          <DropMenuItem
            key={account.id}
            icon={<AccountAvatar account={account} shape="square" size="20" />}
            onAction={() => onSwitchAccount(account.id)}
          >
            {account.display_name}
          </DropMenuItem>
        ))}
        <DropMenuItem
          cacheFor={NAV_PREFETCH_CACHE_FOR}
          href={addAccountHref}
          prefetch="hover"
          icon={<Icon name="add" />}
        >
          {t("components.frames.account_switcher.add_account")}
        </DropMenuItem>
      </DropMenuSection>
      <DropMenuSeparator />
      {mode === "creator" ? (
        <DropMenuItem cacheFor={NAV_PREFETCH_CACHE_FOR} href="/" prefetch="hover" icon={<Icon name="swap" />}>
          {t("components.frames.app_layout.switch_to_explorer")}
        </DropMenuItem>
      ) : (
        <DropMenuItem
          cacheFor={NAV_PREFETCH_CACHE_FOR}
          href={creatorListingsPath()}
          prefetch="hover"
          icon={<Icon name="swap" />}
        >
          {t("components.frames.app_layout.switch_to_creator")}
        </DropMenuItem>
      )}
      <DropMenuSeparator />
      <DropMenuItem
        cacheFor={NAV_PREFETCH_CACHE_FOR}
        href={favoritesPath()}
        prefetch="hover"
        icon={<Icon name="heart1" />}
      >
        {t("components.frames.app_layout.menu_items.favorites")}
      </DropMenuItem>
      <DropMenuItem
        cacheFor={NAV_PREFETCH_CACHE_FOR}
        href={messagesPath()}
        prefetch="hover"
        icon={<Icon name="message" />}
        badgeCount={unreadMessagesCount}
      >
        {t("components.frames.app_layout.menu_items.messages")}
      </DropMenuItem>
      <DropMenuSeparator />
      <DropMenuItem icon={<Icon name="globe" />} onAction={onLanguagesAndCurrency}>
        {t("components.frames.app_layout.menu_items.languages_and_currency")}
      </DropMenuItem>
      <DropMenuItem icon={<Icon name="question" />} onAction={onGetHelp}>
        {t("components.frames.app_layout.menu_items.get_help")}
      </DropMenuItem>
      <DropMenuItem
        cacheFor={NAV_PREFETCH_CACHE_FOR}
        href={accountSettingsPath()}
        prefetch="hover"
        icon={<Icon name="settings" />}
      >
        {t("components.frames.app_layout.menu_items.settings")}
      </DropMenuItem>
      <DropMenuSeparator />
      <DropMenuItem onAction={onLogout}>{t("components.frames.app_layout.menu_items.log_out")}</DropMenuItem>
    </DropMenu>
  );
}
